import { atom } from 'nanostores';

/**
 * Lifecycle of an in-flight ``draft_prior_auth_letter`` tool call, broadcast
 * out to whichever PA wizard is currently watching for its case.
 *
 * The wizard subscribes to this atom in order to drive the "Generate LMN"
 * button between its ``drafting`` and ``idle-ready`` states. We can't read
 * tool-call state directly from the copilot context (the
 * ``CopilotToolUiContextValue`` exposes ``onSendMessage`` but no event
 * stream), so the bridge is: a custom tool-part renderer for
 * ``draft_prior_auth_letter`` writes to this atom on each lifecycle
 * transition, and the wizard reads it.
 *
 * State per case-id:
 *   - ``inflight`` — tool input is committed and the LLM is generating, OR
 *     the executor is still streaming output back.
 *   - ``done`` — output became available (success). Wizard returns to
 *     ``idle-ready`` so the clinician can re-trigger if they want to
 *     regenerate.
 *   - ``error`` — the tool errored. Same UI semantics as ``done`` from the
 *     button's perspective; the chat surfaces the error separately.
 *
 * Keyed by case id (``priorAuthId``). We could key by ``toolCallId`` instead
 * but the wizard only cares about its own case, and a single case never has
 * more than one in-flight draft at a time in practice.
 */
export type LmnDraftLifecycleStateT = 'inflight' | 'done' | 'error';

export interface LmnDraftStatusEntryT {
  caseId: number;
  state: LmnDraftLifecycleStateT;
  /** Monotonic counter so consumers can detect *any* event, even one that
   *  doesn't change ``state`` (e.g. a second ``inflight`` after a retry). */
  seq: number;
}

export const $lmnDraftStatus = atom<Record<number, LmnDraftStatusEntryT>>({});

let _seq = 0;

export function publishLmnDraftStatus(caseId: number, state: LmnDraftLifecycleStateT): void {
  if (!Number.isFinite(caseId) || caseId <= 0) return;
  _seq += 1;
  const next = { ...$lmnDraftStatus.get() };
  next[caseId] = { caseId, state, seq: _seq };
  $lmnDraftStatus.set(next);
}

/** Filename the LMN attach flow uses when uploading the rendered PDF —
 *  see ``dosespot/lmn_attach.py`` (``lmn-pa-{caseId}.pdf``). The
 *  wizard's ``lmnAttached`` heuristic
 *  matches this exact name (rather than just "attachment count grew")
 *  so a clinician manually attaching an unrelated PDF doesn't
 *  accidentally flip the button to ``attached`` state and bypass the
 *  submit-without-LMN guard. */
export function lmnAttachmentFileName(caseId: number): string {
  return `lmn-pa-${caseId}.pdf`;
}

/** Drop the lifecycle entry for a single case.
 *
 *  Used in two places that both need to "forget" a draft after the
 *  clinician has explicitly resolved it:
 *
 *  1. The wizard's "Submit anyway" path — once the clinician acks
 *     submitting without an LMN, the lifecycle stops being relevant
 *     to subsequent ``/submit`` rounds (a follow-up payer question
 *     that re-enters ``ActionRequired`` would otherwise re-trigger
 *     the same guard with the stale ``done`` lifecycle still set).
 *  2. The wizard's 90s drafting safety-net timer — if the tool hung
 *     after firing ``inflight`` and never reaches ``done`` / ``error``,
 *     the timer is the only path that can release the
 *     ``isDrafting`` derivation back to ``idle``; clearing the atom
 *     entry is the matching action to clearing the local
 *     ``awaitingDraftSinceSeq``.
 */
export function clearLmnDraftStatusForCase(caseId: number): void {
  if (!Number.isFinite(caseId) || caseId <= 0) return;
  const current = $lmnDraftStatus.get();
  if (!(caseId in current)) return;
  const next = { ...current };
  delete next[caseId];
  $lmnDraftStatus.set(next);
}

/** Test/dev helper — drops all tracked drafts and resets the seq
 *  counter so test assertions on ``seq`` start from a clean slate.
 *  Resetting only the atom and not the module-level counter would
 *  leak state across test cases; ``_seq`` would keep climbing and
 *  any test that asserts an exact seq value would silently break. */
export function clearLmnDraftStatus(): void {
  _seq = 0;
  $lmnDraftStatus.set({});
}
